<template>
  <div class="mt-6 space-y-6">
    <div v-if="isLoading" class="py-12 text-center">
      <div
        class="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-purple-600 mx-auto"
      ></div>
      <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">Realisatie & prognose laden...</p>
    </div>

    <div v-else-if="error" class="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
      {{ error }}
    </div>

    <template v-else-if="data">
      <HoursRevenueChart
        :data="data"
        :derden-omzet-data="derdenOmzetData"
        :budget-data="budgetData"
        :pipeline-data="pipelineData"
      />
      <CapacityChart
        v-if="capacityData"
        :capacity-data="capacityData"
        :per-team-data="perTeamCapacityData"
        :per-function-data="perFunctionCapacityData"
        :per-team-function-data="perTeamFunctionCapacityData"
        :budget-data="budgetData"
        :months="data.months"
      />
    </template>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { hoursService } from '@/services/hoursService.js'
import { servicesService, budgetService } from '@/services/adminService.js'
import { prospectsLeadsService } from '@/services/prospectService.js'
import HoursRevenueChart from './HoursRevenueChart.vue'
import CapacityChart from './CapacityChart.vue'

const props = defineProps({
  reloadTrigger: { type: Number, default: 0 }
})

const data = ref(null)
const derdenOmzetData = ref(null)
const budgetData = ref([])
const pipelineData = ref(null)
const capacityData = ref(null)
const perTeamCapacityData = ref(null)
const perFunctionCapacityData = ref(null)
const perTeamFunctionCapacityData = ref(null)
const isLoading = ref(false)
const error = ref('')

function addSwpRevenueToDerdenOmzet(swpProspects, months) {
  const swpMonthTotals = {}

  for (const pl of swpProspects) {
    if (!pl.start_date || !pl.expected_closing_date || !pl.expected_revenue || !pl.chance_to_score)
      continue

    const revenue = parseFloat(pl.expected_revenue)
    if (!revenue) continue

    const weighted = revenue * (pl.chance_to_score / 100)
    const start = new Date(pl.start_date)
    const end = new Date(pl.expected_closing_date)
    const totalDays = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1
    if (totalDays <= 0) continue

    const dailyAmount = weighted / totalDays
    const current = new Date(start)
    while (current <= end) {
      const monthKey = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`
      if (months.includes(monthKey)) {
        swpMonthTotals[monthKey] = (swpMonthTotals[monthKey] || 0) + dailyAmount
      }
      current.setDate(current.getDate() + 1)
    }
  }

  // Add SWP revenue as a new revenue group in derden omzet
  const totalSwp = Object.values(swpMonthTotals).reduce((sum, v) => sum + Math.round(v), 0)
  if (totalSwp > 0) {
    const swpGroup = {
      group_name: 'Samenwerkingspartners (prognose)',
      total_derden_omzet: totalSwp,
      months: {}
    }
    for (const m of months) {
      swpGroup.months[m] = { derden_omzet: Math.round(swpMonthTotals[m] || 0) }
    }
    derdenOmzetData.value.revenue_groups.push(swpGroup)

    // Update totals
    derdenOmzetData.value.totals.derden_omzet += totalSwp
    for (const m of months) {
      if (!derdenOmzetData.value.totals.months[m]) {
        derdenOmzetData.value.totals.months[m] = { derden_omzet: 0 }
      }
      derdenOmzetData.value.totals.months[m].derden_omzet += swpGroup.months[m].derden_omzet
    }

    // Ensure all months from the SWP data are in the derden omzet months array
    for (const m of months) {
      if (!derdenOmzetData.value.months.includes(m)) {
        derdenOmzetData.value.months.push(m)
      }
    }
    derdenOmzetData.value.months.sort()
  }
}

function calculatePipelineRevenue(prospects, months) {
  const SOURCING_STATUSES = ['goedgekeurd', 'te_bespreken']
  const leads_verkenningen = {}
  const prospectsMap = {}
  const sourcingMap = {}
  const details = []

  // Exclude prospects whose revenue is either dead (rejected) or already flowing
  // through Simplicate hours/invoicing (project_maken completed) to prevent double-counting.
  // SWP-only prospects go to derden omzet instead.
  const activeProspects = prospects.filter(p => {
    if (p.status === 'OpportunityStatus_closed') return false
    if (p.is_swp_only) return false
    if (p.workflow_status === 'afgekeurd') return false
    if (p.action_status === 'completed') {
      if (p.action_type_code === 'project_maken') return false
      if (p.action_type_code === 'kans_afwijzen') return false
      if (p.action_type_code === 'deal_no_deal' && p.action_outcome === 'no_deal') return false
    }
    return true
  })

  for (const pl of activeProspects) {
    if (!pl.start_date || !pl.expected_closing_date || !pl.expected_revenue || !pl.chance_to_score)
      continue

    const revenue = parseFloat(pl.expected_revenue)
    if (!revenue || !pl.chance_to_score) continue

    const weighted = revenue * (pl.chance_to_score / 100)
    const start = new Date(pl.start_date)
    const end = new Date(pl.expected_closing_date)
    const totalDays = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1
    if (totalDays <= 0) continue

    const isSourcing = SOURCING_STATUSES.includes(pl.workflow_status)
    const isLeadOrVerkenning =
      !isSourcing && (pl.progress_label === 'Lead fase' || pl.progress_label === 'Verkenningsfase')
    const dailyAmount = weighted / totalDays
    const bucket = isSourcing ? sourcingMap : isLeadOrVerkenning ? leads_verkenningen : prospectsMap
    const category = isSourcing
      ? 'sourcing'
      : isLeadOrVerkenning
        ? 'leads_verkenningen'
        : 'prospects'
    const detailMonths = {}

    const current = new Date(start)
    while (current <= end) {
      const monthKey = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`
      if (months.includes(monthKey)) {
        bucket[monthKey] = (bucket[monthKey] || 0) + dailyAmount
        detailMonths[monthKey] = (detailMonths[monthKey] || 0) + dailyAmount
      }
      current.setDate(current.getDate() + 1)
    }

    const name = pl.organization_name
      ? `${pl.subject} - ${pl.organization_name}`
      : pl.subject || 'Onbekend'

    details.push({
      name,
      category,
      months: detailMonths
    })
  }

  return { leads_verkenningen, prospects: prospectsMap, sourcing: sourcingMap, details }
}

async function loadData() {
  isLoading.value = true
  error.value = ''
  try {
    const [
      hoursData,
      derdenData,
      budgets,
      prospects,
      capacity,
      capacityPerTeam,
      capacityPerFunction,
      capacityPerTeamFunction
    ] = await Promise.all([
      hoursService.getRevenuePerTeam(),
      servicesService.getDerdenOmzet(),
      budgetService.getAll().catch(() => []),
      prospectsLeadsService.getAll().catch(() => []),
      hoursService.getCapacityMetrics().catch(() => null),
      hoursService.getCapacityMetricsPerTeam().catch(() => null),
      hoursService.getCapacityMetricsPerFunction().catch(() => null),
      hoursService.getCapacityMetricsPerTeamAndFunction().catch(() => null)
    ])
    data.value = hoursData
    derdenOmzetData.value = derdenData
    budgetData.value = budgets
    capacityData.value = capacity
    perTeamCapacityData.value = capacityPerTeam
    perFunctionCapacityData.value = capacityPerFunction
    perTeamFunctionCapacityData.value = capacityPerTeamFunction
    if (hoursData?.months?.length && prospects?.length) {
      pipelineData.value = calculatePipelineRevenue(prospects, hoursData.months)

      // Add SWP-only prospect revenue to derden omzet
      const swpProspects = prospects.filter(
        p => p.is_swp_only && p.status !== 'OpportunityStatus_closed'
      )
      if (swpProspects.length > 0 && derdenOmzetData.value) {
        addSwpRevenueToDerdenOmzet(swpProspects, hoursData.months)
      }
    }
  } catch {
    error.value = 'Kon realisatie & prognose data niet laden'
  } finally {
    isLoading.value = false
  }
}

watch(
  () => props.reloadTrigger,
  () => {
    loadData()
  }
)

onMounted(loadData)
</script>
